import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SimulationResponse } from '@/types/simulation.ts';
import { useCatalog, useCatalogLabelMap } from '@/hooks/useCatalog.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';

export type GroupByKey =
  | 'none'
  | 'simulationType'
  | 'salePhase'
  | 'sector'
  | 'difficultyLevel'
  | 'assignedSalespersonIds'
  | 'maestro';

export interface SimulationGroup {
  key: string;
  label: string;
  simulations: SimulationResponse[];
}

/**
 * Groups simulations by the given criterion.
 * Returns only groups that contain at least one simulation.
 * When `reverseGroups` is true the natural group order is inverted
 * (e.g. difficulty 5→1 instead of 1→5).
 */
export function useGroupedSimulations(
  simulations: SimulationResponse[],
  groupBy: GroupByKey,
  salespeopleMap?: Map<string, string>,
  reverseGroups?: boolean,
): SimulationGroup[] {
  const { t } = useTranslation();

  const { data: simulationTypeEntries } = useCatalog('SimulationType');
  const { data: salePhaseEntries } = useCatalog('SalePhase');
  const { data: sectorEntries } = useCatalog('Sector');
  const simulationTypeLabels = useCatalogLabelMap('SimulationType');
  const salePhaseLabels = useCatalogLabelMap('SalePhase');
  const sectorLabels = useCatalogLabelMap('Sector');
  const difficultyLabels = useCatalogLabelMap('Difficulty');

  return useMemo(() => {
    let groups: SimulationGroup[];

    const orderedSlugs = (entries: { slug: string; sortOrder: number }[] | undefined): string[] =>
      [...(entries ?? [])].sort((a, b) => a.sortOrder - b.sortOrder).map((e) => e.slug);

    if (groupBy === 'none') {
      groups = [{ key: '__all__', label: '', simulations }];
    } else if (groupBy === 'simulationType') {
      groups = groupByOrdered(
        simulations,
        (s) => [s.simulationType],
        orderedSlugs(simulationTypeEntries),
        (key) => simulationTypeLabels.get(key) ?? key,
      );
    } else if (groupBy === 'salePhase') {
      groups = groupByOrdered(
        simulations,
        (s) => [s.salePhase],
        orderedSlugs(salePhaseEntries),
        (key) => salePhaseLabels.get(key) ?? key,
      );
    } else if (groupBy === 'sector') {
      groups = groupByOrdered(
        simulations,
        (s) => [s.sector || '__none__'],
        [...orderedSlugs(sectorEntries), '__none__'],
        (key) => (key === '__none__' ? t('quickPractice.noSector') : sectorLabels.get(key) ?? key),
      );
    } else if (groupBy === 'difficultyLevel') {
      groups = groupByOrdered(
        simulations,
        (s) => [String(s.difficultyLevel)],
        ['1', '2', '3', '4', '5'],
        (key) => difficultyLabels.get(key) ?? key,
      );
    } else if (groupBy === 'assignedSalespersonIds') {
      groups = groupByOrdered(
        simulations,
        (s) => s.assignedSalespersonIds.length > 0 ? s.assignedSalespersonIds : ['__none__'],
        salespeopleMap ? [...salespeopleMap.keys(), '__none__'] : ['__none__'],
        (key) => {
          if (key === '__none__') return t('groupBy.unassigned', 'Sin asignar');
          return salespeopleMap?.get(key) ?? key;
        },
      );
    } else if (groupBy === 'maestro') {
      const maestroOrder = maestros.map((m) => m.id);
      const maestroNameMap = new Map(maestros.map((m) => [m.id, getMaestroName(m)]));
      groups = groupByOrdered(
        simulations,
        (s) => [s.assignedMaestroId || '__none__'],
        [...maestroOrder, '__none__'],
        (key) => (key === '__none__' ? t('simulation.maestroToChoose') : maestroNameMap.get(key) ?? key),
      );
    } else {
      groups = [{ key: '__all__', label: '', simulations }];
    }

    if (reverseGroups && groups.length > 1) {
      groups.reverse();
    }

    return groups;
  }, [
    simulations,
    groupBy,
    salespeopleMap,
    reverseGroups,
    t,
    simulationTypeEntries,
    salePhaseEntries,
    sectorEntries,
    simulationTypeLabels,
    salePhaseLabels,
    sectorLabels,
    difficultyLabels,
  ]);
}

/** Group simulations using an ordered key list. Only non-empty groups are returned. */
function groupByOrdered(
  simulations: SimulationResponse[],
  getKeys: (sim: SimulationResponse) => string[],
  orderedKeys: string[],
  getLabel: (key: string) => string,
): SimulationGroup[] {
  const buckets = new Map<string, SimulationResponse[]>();

  for (const sim of simulations) {
    for (const key of getKeys(sim)) {
      const list = buckets.get(key);
      if (list) {
        list.push(sim);
      } else {
        buckets.set(key, [sim]);
      }
    }
  }

  const groups: SimulationGroup[] = [];

  // First add keys in the defined order
  for (const key of orderedKeys) {
    const sims = buckets.get(key);
    if (sims && sims.length > 0) {
      groups.push({ key, label: getLabel(key), simulations: sims });
      buckets.delete(key);
    }
  }

  // Then add any remaining keys not in the predefined order
  for (const [key, sims] of buckets) {
    if (sims.length > 0) {
      groups.push({ key, label: getLabel(key), simulations: sims });
    }
  }

  return groups;
}
